
import { useState, useRef, useEffect } from 'react';

interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
  onClose: () => void;
}

const EMOJI_CATEGORIES: { label: string; icon: string; emojis: string[] }[] = [
  {
    label: 'Smileys',
    icon: 'ri-emotion-happy-line',
    emojis: [
      '😀','😃','😄','😁','😆','😅','🤣','😂','🙂','😊',
      '😇','🥰','😍','🤩','😘','😗','😚','😙','🥲','😋',
      '😛','😜','🤪','😝','🤑','🤗','🤭','🫢','🤫','🤔',
      '🫡','🤐','🤨','😐','😑','😶','🫥','😏','😒','🙄',
      '😬','🤥','😌','😔','😪','🤤','😴','😷','🤒','🤕',
      '🤢','🤮','🥵','🥶','🥴','😵','🤯','🤠','🥳','🥸',
      '😎','🤓','🧐','😕','🫤','😟','🙁','😮','😯','😲',
      '😳','🥺','🥹','😦','😧','😨','😰','😥','😢','😭',
      '😱','😖','😣','😞','😓','😩','😫','🥱','😤','😡',
      '😠','🤬','😈','👿','💀','☠️','💩','🤡','👹','👺',
    ],
  },
  {
    label: 'Gestures',
    icon: 'ri-hand-heart-line',
    emojis: [
      '👋','🤚','🖐️','✋','🖖','🫱','🫲','🫳','🫴','👌',
      '🤌','🤏','✌️','🤞','🫰','🤟','🤘','🤙','👈','👉',
      '👆','🖕','👇','☝️','🫵','👍','👎','✊','👊','🤛',
      '🤜','👏','🙌','🫶','👐','🤲','🤝','🙏','💪','🦾',
      '❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔',
      '❤️‍🔥','❤️‍🩹','💕','💞','💓','💗','💖','💘','💝','💟',
    ],
  },
  {
    label: 'Animals',
    icon: 'ri-bear-smile-line',
    emojis: [
      '🐶','🐱','🐭','🐹','🐰','🦊','🐻','🐼','🐻‍❄️','🐨',
      '🐯','🦁','🐮','🐷','🐸','🐵','🙈','🙉','🙊','🐒',
      '🐔','🐧','🐦','🐤','🦆','🦅','🦉','🦇','🐺','🐗',
      '🐴','🦄','🐝','🪱','🐛','🦋','🐌','🐞','🐜','🪰',
      '🌸','🌺','🌻','🌹','🌷','🌼','💐','🌿','☘️','🍀',
    ],
  },
  {
    label: 'Food',
    icon: 'ri-restaurant-line',
    emojis: [
      '🍎','🍐','🍊','🍋','🍌','🍉','🍇','🍓','🫐','🍈',
      '🍒','🍑','🥭','🍍','🥥','🥝','🍅','🍆','🥑','🥦',
      '🌶️','🫑','🥒','🥬','🧄','🧅','🥔','🍠','🥐','🍞',
      '🥖','🥨','🧀','🥚','🍳','🧈','🥞','🧇','🥓','🍔',
      '🍟','🍕','🌭','🥪','🌮','🌯','🫔','🥙','🧆','🥘',
      '☕','🍵','🧃','🥤','🍶','🍺','🍻','🥂','🍷','🍸',
    ],
  },
  {
    label: 'Objects',
    icon: 'ri-lightbulb-line',
    emojis: [
      '⚽','🏀','🏈','⚾','🥎','🎾','🏐','🏉','🥏','🎱',
      '🎮','🕹️','🎲','🧩','🎭','🎨','🎬','🎤','🎧','🎼',
      '🎹','🥁','🎷','🎺','🎸','🪕','🎻','🎪','🎟️','🏆',
      '🥇','🥈','🥉','🏅','🎖️','🎗️','🎁','🎀','🎊','🎉',
      '🔥','⭐','🌟','✨','💫','🎆','🎇','🧨','💥','💢',
      '💯','🏠','🏡','🏢','🏣','🏥','🏦','🏪','🏫','🏬',
    ],
  },
];

const RECENT_KEY = 'emoji-recent';

const getRecentEmojis = (): string[] => {
  try {
    const stored = localStorage.getItem(RECENT_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveRecentEmoji = (emoji: string) => {
  try {
    const recent = getRecentEmojis().filter((e) => e !== emoji);
    recent.unshift(emoji);
    localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, 24)));
  } catch {
    // ignore
  }
};

export default function EmojiPicker({ onSelect, onClose }: EmojiPickerProps) {
  const [activeCategory, setActiveCategory] = useState(0);
  const [search, setSearch] = useState('');
  const pickerRef = useRef<HTMLDivElement>(null);
  const recentEmojis = getRecentEmojis();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const handleSelect = (emoji: string) => {
    saveRecentEmoji(emoji);
    onSelect(emoji);
  };

  const allEmojis = EMOJI_CATEGORIES.flatMap((c) => c.emojis);
  const filteredEmojis = search
    ? allEmojis.filter(() => true).slice(0, 60) // simple: show all when searching (emoji names not available)
    : null;

  return (
    <div
      ref={pickerRef}
      className="absolute bottom-full left-0 mb-2 w-[320px] bg-white rounded-xl shadow-xl border border-gray-200 z-30 overflow-hidden"
      style={{ maxHeight: 360 }}
    >
      {/* Search */}
      <div className="p-2 border-b border-gray-100">
        <div className="relative">
          <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search emoji..."
            className="w-full pl-8 pr-3 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400/30"
          />
        </div>
      </div>

      {/* Category tabs */}
      {!search && (
        <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-gray-100 overflow-x-auto">
          {recentEmojis.length > 0 && (
            <button
              onClick={() => setActiveCategory(-1)}
              className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-md text-sm transition-colors cursor-pointer ${
                activeCategory === -1
                  ? 'bg-teal-100 text-teal-600'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
              title="Recent"
            >
              <i className="ri-time-line"></i>
            </button>
          )}
          {EMOJI_CATEGORIES.map((cat, i) => (
            <button
              key={cat.label}
              onClick={() => setActiveCategory(i)}
              className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-md text-sm transition-colors cursor-pointer ${
                activeCategory === i
                  ? 'bg-teal-100 text-teal-600'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
              title={cat.label}
            >
              <i className={cat.icon}></i>
            </button>
          ))}
        </div>
      )}

      {/* Emoji grid */}
      <div className="p-2 overflow-y-auto" style={{ maxHeight: 240 }}>
        {search ? (
          <div className="grid grid-cols-8 gap-0.5">
            {allEmojis.slice(0, 80).map((emoji, i) => (
              <button
                key={`${emoji}-${i}`}
                onClick={() => handleSelect(emoji)}
                className="w-8 h-8 flex items-center justify-center text-lg hover:bg-gray-100 rounded-md cursor-pointer transition-colors"
              >
                {emoji}
              </button>
            ))}
          </div>
        ) : activeCategory === -1 && recentEmojis.length > 0 ? (
          <>
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1 px-1">
              Recently Used
            </p>
            <div className="grid grid-cols-8 gap-0.5">
              {recentEmojis.map((emoji, i) => (
                <button
                  key={`recent-${emoji}-${i}`}
                  onClick={() => handleSelect(emoji)}
                  className="w-8 h-8 flex items-center justify-center text-lg hover:bg-gray-100 rounded-md cursor-pointer transition-colors"
                >
                  {emoji}
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1 px-1">
              {EMOJI_CATEGORIES[activeCategory]?.label}
            </p>
            <div className="grid grid-cols-8 gap-0.5">
              {EMOJI_CATEGORIES[activeCategory]?.emojis.map((emoji, i) => (
                <button
                  key={`${emoji}-${i}`}
                  onClick={() => handleSelect(emoji)}
                  className="w-8 h-8 flex items-center justify-center text-lg hover:bg-gray-100 rounded-md cursor-pointer transition-colors"
                >
                  {emoji}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
